import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useCurrency } from '../context/CurrencyContext';

function OrderList({ merchantId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const currencySymbol = useCurrency();

  const fetchOrders = () => {
    const tg = window.Telegram.WebApp;
    axios.get('/api/merchant/orders', {
      headers: { 'x-telegram-init-data': tg.initData }
    }).then(res => {
      setOrders(res.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // 根据日期范围计算开始日期
  const getStartDate = () => {
    if (dateRange === 'custom') {
      return customStartDate;
    }
    const today = new Date();
    switch (dateRange) {
      case 'today':
        return today.toISOString().split('T')[0];
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return weekAgo.toISOString().split('T')[0];
      case 'month':
        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 30);
        return monthAgo.toISOString().split('T')[0];
      default:
        return '';
    }
  };

  const getEndDate = () => {
    if (dateRange === 'custom') {
      return customEndDate;
    }
    const today = new Date();
    if (dateRange === 'today' || dateRange === 'week' || dateRange === 'month') {
      return today.toISOString().split('T')[0];
    }
    return '';
  };

  // 筛选订单
  const filteredOrders = orders.filter(order => {
    // 状态筛选
    if (filterStatus !== 'all' && order.status !== filterStatus) {
      return false;
    }
    // 日期筛选
    const startDate = getStartDate();
    const endDate = getEndDate();
    const orderDate = new Date(order.created_at).toISOString().split('T')[0];
    
    if (startDate && orderDate < startDate) return false;
    if (endDate && orderDate > endDate) return false;
    return true;
  });

  // 统计
  const stats = {
    totalOrders: filteredOrders.length,
    totalAmount: filteredOrders.reduce((sum, o) => sum + parseFloat(o.total_amount), 0),
    totalItems: filteredOrders.reduce((sum, o) => {
      const itemsCount = o.items ? o.items.reduce((s, i) => s + i.quantity, 0) : 0;
      return sum + itemsCount;
    }, 0)
  };

const confirmOrder = async (orderId) => {
  const fee = prompt(`请输入配送费（${currencySymbol}）:`, '0');
  if (fee === null) return;
  const deliveryFee = parseFloat(fee);
  if (isNaN(deliveryFee) || deliveryFee < 0) {
    alert('请输入有效的配送费');
    return;
  }
  const tg = window.Telegram.WebApp;
  // 乐观更新
  setOrders(prevOrders =>
    prevOrders.map(order =>
      order.id === orderId ? { ...order, status: 'confirmed', delivery_fee: deliveryFee } : order
    )
  );
  try {
    await axios.put(`/api/merchant/orders/${orderId}/confirm`, { delivery_fee: deliveryFee }, {
      headers: { 'x-telegram-init-data': tg.initData }
    });
    fetchOrders();
  } catch (err) {
    if (err.response) {
      fetchOrders(); // 后端已处理，刷新列表
    } else {
      alert('网络错误，请重试');
      fetchOrders();
    }
  }
};

const updateStatus = async (orderId, status) => {
  const statusMap = {
    'pending': '待确认',
    'confirmed': '已确认',
    'preparing': '备餐中',
    'delivered': '已送达',
    'cancelled': '已取消'
  };
  if (!window.confirm(`确定将订单状态改为 ${statusMap[status] || status} 吗？`)) {
    return;
  }
  const tg = window.Telegram.WebApp;
  // 乐观更新：立即修改本地状态
  setOrders(prevOrders =>
    prevOrders.map(order =>
      order.id === orderId ? { ...order, status: status } : order
    )
  );
  try {
    await axios.put(`/api/merchant/orders/${orderId}/status`, { status }, {
      headers: { 'x-telegram-init-data': tg.initData }
    });
    // 请求成功（状态码 2xx）时，刷新列表以确保数据完整
    fetchOrders();
  } catch (err) {
    // 如果有响应对象，说明后端已处理（即使状态码不是 2xx）
    if (err.response) {
      // 静默处理，不弹出错误，仅刷新列表
      fetchOrders();
    } else {
      // 真正的网络错误（请求未发出或连接中断）
      alert('网络错误，请检查连接后重试');
      fetchOrders(); // 回滚乐观更新
    }
  }
};

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>加载中...</div>;

  return (
    <div style={{ padding: '12px' }}>
      <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>订单管理</h2>
      
      {/* 筛选栏 */}
      <div style={{
        background: '#f5f5f5',
        borderRadius: '10px',
        padding: '10px',
        marginBottom: '12px'
      }}>
        {/* 第一行：筛选条件 */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>时间：</span>
            <select 
              value={dateRange} 
              onChange={e => {
                setDateRange(e.target.value);
                if (e.target.value !== 'custom') {
                  setCustomStartDate('');
                  setCustomEndDate('');
                }
              }}
              style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff' }}
            >
              <option value="all">全部订单</option>
              <option value="today">今日订单</option>
              <option value="week">最近7天</option>
              <option value="month">最近30天</option>
              <option value="custom">自定义</option>
            </select>
          </div>
          
          {dateRange === 'custom' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>从</span>
                <input 
                  type="date" 
                  value={customStartDate} 
                  onChange={e => setCustomStartDate(e.target.value)}
                  style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>到</span>
                <input 
                  type="date" 
                  value={customEndDate} 
                  onChange={e => setCustomEndDate(e.target.value)}
                  style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
            </>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>状态：</span>
            <select 
              value={filterStatus} 
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff' }}
            >
              <option value="all">全部状态</option>
              <option value="pending">待确认</option>
              <option value="confirmed">已确认</option>
              <option value="preparing">备餐中</option>
              <option value="delivered">已送达</option>
              <option value="cancelled">已取消</option>
            </select>
          </div>
        </div>
        
        {/* 第二行：统计信息 */}
        <div style={{
          background: '#fff',
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          justifyContent: 'space-around',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff6b6b' }}>{stats.totalOrders}</div>
            <div style={{ fontSize: '10px', color: '#666' }}>订单数</div>
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff6b6b' }}>{stats.totalItems}</div>
            <div style={{ fontSize: '10px', color: '#666' }}>总件数</div>
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff6b6b' }}>{currencySymbol} {stats.totalAmount.toFixed(2)}</div>
            <div style={{ fontSize: '10px', color: '#666' }}>总金额</div>
          </div>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999' }}>暂无订单</p>
      ) : (
        filteredOrders.map(o => (
          <div key={o.id} style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#666' }}>订单号</div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{o.order_number}</div>
                <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <span style={{
                background: o.status === 'pending' ? '#ff9800' : o.status === 'confirmed' ? '#4caf50' : o.status === 'preparing' ? '#2196f3' : o.status === 'delivered' ? '#9e9e9e' : '#f44336',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '16px',
                fontSize: '10px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                {o.status === 'pending' ? '待确认' : o.status === 'confirmed' ? '已确认' : o.status === 'preparing' ? '备餐中' : o.status === 'delivered' ? '已送达' : '已取消'}
              </span>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#666' }}>商品明细</div>
              {o.items && o.items.length > 0 ? (
                o.items.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '2px 0' }}>
                    <span>{item.product_name} x {item.quantity}</span>
                    <span>{currencySymbol} {(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '12px', color: '#999' }}>-</div>
              )}
            </div>

            <div style={{ marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid #eee', fontSize: '13px', fontWeight: 'bold' }}>
              总金额：<span style={{ color: '#ff6b6b' }}>{currencySymbol} {o.total_amount}</span>
              {o.delivery_fee > 0 && <span style={{ fontSize: '11px', marginLeft: '8px', fontWeight: 'normal' }}>配送费: {currencySymbol} {o.delivery_fee}</span>}
            </div>

            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
              <span style={{ color: '#666' }}>用户：</span>
              {o.username ? (
                <a href={`https://t.me/${o.username}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2196f3', textDecoration: 'none' }}>
                  @{o.username}
                </a>
              ) : (
                <span>用户 {o.user_id}</span>
              )}
            </div>

            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
              <span style={{ color: '#666' }}>地址：</span>
              {o.delivery_address || o.address_text || '未填写'}
              {o.address_note && <span style={{ color: '#999' }}> ({o.address_note})</span>}
            </div>

            <div style={{ fontSize: '12px', marginBottom: '12px' }}>
              <span style={{ color: '#666' }}>支付：</span>
              {o.payment_method || '现金'}
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {o.status === 'pending' && (
                <>
                  <button onClick={() => confirmOrder(o.id)} style={{ background: '#4caf50', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>确认接单</button>
                  <button onClick={() => updateStatus(o.id, 'cancelled')} style={{ background: '#f44336', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>拒绝订单</button>
                </>
              )}
              {o.status === 'confirmed' && (
                <button onClick={() => updateStatus(o.id, 'preparing')} style={{ background: '#2196f3', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>开始备餐</button>
              )}
              {o.status === 'preparing' && (
                <button onClick={() => updateStatus(o.id, 'delivered')} style={{ background: '#ff9800', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>标记送达</button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default OrderList;